import React, { useEffect, useState, useRef } from 'react';
import { 
    ConnectCatchTheBallWebSocket, 
    DisconnectCatchTheBallWebSocket, 
    JoinCatchTheBallGame, 
    SendGamepadCommand 
} from '../../../utilities/BL/CatchTheBall';
import { ValidateSqlInjection as BLValidateSqlInjection } from '../../../utilities/BL/Security';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';


const CatchTheBall = () => {
    const { showMessage } = useFloatMessage();

    // ==========================================================================
    // ESTADOS
    // ==========================================================================
    const [step, setStep] = useState('register'); // register, playing, done
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [isLoading, setIsLoading] = useState(false);
    const [finalScore, setFinalScore] = useState(0);

    const [userData, setUserData] = useState({
        fullName: '',
        nickname: '',
        email: '',
        phoneNumber: '',
        companionType: ''
    });

    const activeCommandRef = useRef(null);

    // ==========================================================================
    // WEBSOCKET (Escucha al Servidor/Pantalla)
    // ==========================================================================
    useEffect(() => {
        ConnectCatchTheBallWebSocket(setConnectionStatus, (data) => {
            // Cuando la partida termina
            if (data.action === 'game_over') {
                SendGamepadCommand('stop');
                setFinalScore(data.payload?.score || 0);
                setStep('done');
            }
            
            // NUEVO: Cuando el Admin da una "Segunda Oportunidad" desde cabina
            // o cuando el mismo celular registra el juego.
            if (data.action === 'player_ready') {
                setStep('playing');
            }
        });

        // Prevenir zoom y scroll en la tablet mientras juegan
        const preventDefault = (e) => { if (step === 'playing') e.preventDefault(); };
        document.addEventListener('touchmove', preventDefault, { passive: false });

        return () => {
            DisconnectCatchTheBallWebSocket();
            document.removeEventListener('touchmove', preventDefault);
        };
    }, [step]);

    // ==========================================================================
    // MANEJO DE FORMULARIO
    // ==========================================================================
    const handleInputChange = (e) => {
        setUserData({ ...userData, [e.target.name]: e.target.value });
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        
        if (!userData.fullName || !userData.nickname) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor, completa ambos campos.` });
            return;
        }

        setIsLoading(true);

        const isNameThreat = await BLValidateSqlInjection(userData.fullName);
        const isNickThreat = await BLValidateSqlInjection(userData.nickname);

        if (isNameThreat || isNickThreat) {
            setIsLoading(false);
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Caracteres no permitidos en el formulario.` });
            return; 
        }

        const res = await JoinCatchTheBallGame(userData);
        setIsLoading(false);

        if (res.success) {
            setStep('playing');
        } else {
            showMessage({ type: MESSAGE_TYPE.BAD, text: `Error de conexión. Intenta de nuevo.` });
        }
    };

    // ==========================================================================
    // CONTROLES DEL GAMEPAD (Cero Lag)
    // ==========================================================================
    const handleMove = (direction) => {
        if (activeCommandRef.current === direction) return;
        activeCommandRef.current = direction;
        SendGamepadCommand(direction); 
    };

    const handleStop = () => {
        if (activeCommandRef.current === 'stop') return;
        activeCommandRef.current = 'stop';
        SendGamepadCommand('stop');
    };

    // Función extra si el Staff con la tablet decide dar la 2da Oportunidad
    const handlePlayAgainLocally = async () => {
        setIsLoading(true);
        const res = await JoinCatchTheBallGame(userData); 
        setIsLoading(false);
        if (res.success) setStep('playing');
    };

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    return (
        <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#f3f4f6', position: 'relative' }}>
            
            {connectionStatus !== 'connected' && (
                <div style={{ position: 'absolute', top: 0, width: '100%', background: '#dc3545', color: '#fff', textAlign: 'center', zIndex: 9999, padding: '2px', fontSize: '12px' }}>
                    Conectando al servidor...
                </div>
            )}

            {/* PANTALLA 1: REGISTRO SIMPLIFICADO */}
            {step === 'register' && (
                <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5 overflow-auto"> 
                    <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-4" alt="Logo" />
                    <p className="SelfieFan__Fan__Title text-center mt-3">¡Atrapa los Balones!</p>
                    <p className='Messages__Fan__Text--Center'>Regístrate para jugar en la pantalla gigante.</p>
                    <hr />

                    <label className='PluckingRooster__Instructions__Form__Labels mt-3'>Nombre Completo</label>
                    <input 
                        name="fullName" 
                        value={userData.fullName} 
                        onChange={handleInputChange} 
                        className="PluckingRooster__Instructions__Form__Inputs" 
                        placeholder="Ej. Juan Pérez"
                    />

                    <label className='PluckingRooster__Instructions__Form__Labels text-danger fw-bold'>Apodo (Saldrá en la pantalla gigante)</label>
                    <input 
                        name="nickname" 
                        value={userData.nickname} 
                        onChange={handleInputChange} 
                        className="PluckingRooster__Instructions__Form__Inputs border-danger" 
                        maxLength="12" 
                        placeholder="Máx. 12 letras" 
                    />

                    <button 
                        onClick={handleRegister} 
                        className={`Btn__Adsl--Red PluckingRooster__Instructions__Form__Button mt-5 ${isLoading ? 'opacity-50' : ''}`}
                        disabled={isLoading || connectionStatus !== 'connected'}
                    >
                        {isLoading ? 'Conectando...' : 'Jugar ahora'}
                    </button>

                    <footer className="text-center mt-5 mb-4 FooterCredits">
                        Hecho con ♥ por Interaktia | 2026
                    </footer>
                </div>
            )}

            {/* PANTALLA 2: GAMEPAD */}
            {step === 'playing' && (
                <div style={{ display: 'flex', width: '100%', height: '100%', userSelect: 'none' }}>
                    {/* BOTÓN IZQUIERDA */}
                    <div 
                        style={{ flex: 1, backgroundColor: '#2980b9', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRight: '2px solid rgba(0,0,0,0.3)', cursor: 'pointer' }}
                        onTouchStart={() => handleMove('left')}
                        onTouchEnd={handleStop}
                        onTouchCancel={handleStop} // <- Detecta si el celular interrumpe el toque
                        onContextMenu={(e) => e.preventDefault()} // <- Bloquea el menú de opciones nativo
                        onMouseDown={() => handleMove('left')}
                        onMouseUp={handleStop}
                        onMouseLeave={handleStop}
                    >
                        <i className="bi bi-caret-left-fill" style={{ fontSize: '10rem', color: 'rgba(255,255,255,0.5)' }}></i>
                    </div>

                    {/* BOTÓN DERECHA */}
                    <div 
                        style={{ flex: 1, backgroundColor: '#c0392b', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
                        onTouchStart={() => handleMove('right')}
                        onTouchEnd={handleStop}
                        onTouchCancel={handleStop} // <- Detecta si el celular interrumpe el toque
                        onContextMenu={(e) => e.preventDefault()} // <- Bloquea el menú de opciones nativo
                        onMouseDown={() => handleMove('right')}
                        onMouseUp={handleStop}
                        onMouseLeave={handleStop}
                    >
                        <i className="bi bi-caret-right-fill" style={{ fontSize: '10rem', color: 'rgba(255,255,255,0.5)' }}></i>
                    </div>

                    <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
                        <p style={{ background: 'rgba(0,0,0,0.5)', color: 'white', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold' }}>
                            ¡MIRA LA PANTALLA GIGANTE!
                        </p>
                    </div>
                </div>
            )}

            {/* PANTALLA 3: RESULTADO FINAL Y DECISIÓN */}
            {step === 'done' && (
                <div className="SelfieFan__Sent container h-100 d-flex flex-column justify-content-center">
                    <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
                    <p className="fs-1 mb-0 text-success text-center"><i className="bi bi-check-circle-fill"></i></p>
                    <p className="SelfieFan__Sent__Title mt-3 mb-0 text-center">¡Partida Terminada!</p>
                    <p className='Messages__Fan__Text--Center mb-0 mt-3'>Puntaje obtenido:</p>
                    <p className='text-center fw-black' style={{ fontSize: '4rem', color: '#e74c3c', lineHeight: 1 }}>{finalScore}</p>
                    
                    <div className="d-flex flex-column gap-3 mt-5">
                        <button onClick={handlePlayAgainLocally} className="Btn__Adsl--RedOutline" disabled={isLoading}>
                            <i className="bi bi-arrow-clockwise"></i> Dar segunda oportunidad
                        </button>
                        <button 
                            onClick={() => {
                                setUserData({ fullName: '', nickname: '', email: '', phoneNumber: '', companionType: '' });
                                setStep('register');
                            }} 
                            className="Btn__Adsl--Red"
                        >
                            Siguiente Jugador <i className="bi bi-arrow-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export { CatchTheBall };









// import React, { useEffect, useState, useRef } from 'react'
// import { ConnectCatchTheBallWebSocket, DisconnectCatchTheBallWebSocket, JoinCatchTheBallGame, SendGamepadCommand } from '../../../utilities/BL/CatchTheBall'
// import { ValidateSqlInjection as BLValidateSqlInjection } from '../../../utilities/BL/Security'
// import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage'


// const CatchTheBall = () => {
//     const { showMessage } = useFloatMessage();

//     // ==========================================================================
//     // ESTADOS
//     // ==========================================================================
//     const [step, setStep] = useState('register'); // register, playing, done
//     const [connectionStatus, setConnectionStatus] = useState('idle');
//     const [isLoading, setIsLoading] = useState(false);
//     const [finalScore, setFinalScore] = useState(0);

//     // Mantenemos la estructura completa para que el Backend no se queje, 
//     // pero los campos que no usamos se irán en blanco.
//     const [userData, setUserData] = useState({
//         fullName: '',
//         nickname: '',
//         email: '',
//         phoneNumber: '',
//         companionType: ''
//     });

//     const activeCommandRef = useRef(null); // Evita mandar spam del mismo comando

//     // ==========================================================================
//     // WEBSOCKET (Escucha a la pantalla gigante)
//     // ==========================================================================
//     useEffect(() => {
//         ConnectCatchTheBallWebSocket(setConnectionStatus, (data) => {
//             if (data.action === 'game_over') {
//                 SendGamepadCommand('stop');
//                 setFinalScore(data.payload?.score || 0);
//                 setStep('done');

//                 // Después de 5 segundos, regresamos al registro para el siguiente en la fila
//                 // setTimeout(() => {
//                 //     setUserData({ fullName: '', nickname: '', email: '', phoneNumber: '', companionType: '' });
//                 //     setStep('register');
//                 // }, 5000);
//             }
//         });

//         // Prevenir zoom y scroll en la tablet mientras juegan
//         const preventDefault = (e) => { if (step === 'playing') e.preventDefault(); };
//         document.addEventListener('touchmove', preventDefault, { passive: false });

//         return () => {
//             DisconnectCatchTheBallWebSocket();
//             document.removeEventListener('touchmove', preventDefault);
//         };
//     }, [step]);

//     // ==========================================================================
//     // MANEJO DE FORMULARIO
//     // ==========================================================================
//     const handleInputChange = (e) => {
//         setUserData({ ...userData, [e.target.name]: e.target.value });
//     };

//     const handleRegister = async (e) => {
//         e.preventDefault();
        
//         // AHORA SOLO VALIDAMOS NOMBRE Y APODO
//         if (!userData.fullName || !userData.nickname) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: `Por favor, completa ambos campos.` });
//             return;
//         }

//         setIsLoading(true);

//         const isNameThreat = await BLValidateSqlInjection(userData.fullName);
//         const isNickThreat = await BLValidateSqlInjection(userData.nickname);

//         if (isNameThreat || isNickThreat) {
//             setIsLoading(false);
//             showMessage({ type: MESSAGE_TYPE.BAD, text: `Caracteres no permitidos en el formulario.` });
//             return; 
//         }

//         const res = await JoinCatchTheBallGame(userData);
//         setIsLoading(false);

//         if (res.success) {
//             setStep('playing');
//         } else {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: `Error de conexión. Intenta de nuevo.` });
//         }
//     };

//     // ==========================================================================
//     // CONTROLES DEL GAMEPAD
//     // ==========================================================================
//     const handleMove = (direction) => {
//         if (activeCommandRef.current === direction) return;
//         activeCommandRef.current = direction;
//         SendGamepadCommand(direction); 
//     };

//     const handleStop = () => {
//         if (activeCommandRef.current === 'stop') return;
//         activeCommandRef.current = 'stop';
//         SendGamepadCommand('stop');
//     };

//     const handlePlayAgain = async () => {
//         setIsLoading(true);
//         const res = await JoinCatchTheBallGame(userData); // Reutiliza la info que ya llenó
//         setIsLoading(false);
//         if (res.success) setStep('playing');
//     };

//     // ==========================================================================
//     // RENDERIZADO
//     // ==========================================================================
//     return (
//         <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', backgroundColor: '#f3f4f6', position: 'relative' }}>
            
//             {connectionStatus !== 'connected' && (
//                 <div style={{ position: 'absolute', top: 0, width: '100%', background: '#dc3545', color: '#fff', textAlign: 'center', zIndex: 9999, padding: '2px', fontSize: '12px' }}>
//                     Conectando al servidor...
//                 </div>
//             )}

//             {/* PANTALLA 1: REGISTRO SIMPLIFICADO */}
//             {step === 'register' && (
//                 <div className="container flex flex-col justify-center h-full bg-gray-100 pb-5 overflow-auto"> 
//                     <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-4" alt="Logo" />
//                     <p className="SelfieFan__Fan__Title text-center mt-3">¡Atrapa los Balones!</p>
//                     <p className='Messages__Fan__Text--Center'>Regístrate para jugar en la pantalla gigante.</p>
//                     <hr />

//                     <label className='PluckingRooster__Instructions__Form__Labels mt-3'>Nombre Completo</label>
//                     <input 
//                         name="fullName" 
//                         value={userData.fullName} 
//                         onChange={handleInputChange} 
//                         className="PluckingRooster__Instructions__Form__Inputs" 
//                         placeholder="Ej. Juan Pérez"
//                     />

//                     <label className='PluckingRooster__Instructions__Form__Labels text-danger fw-bold'>Apodo (Saldrá en la pantalla gigante)</label>
//                     <input 
//                         name="nickname" 
//                         value={userData.nickname} 
//                         onChange={handleInputChange} 
//                         className="PluckingRooster__Instructions__Form__Inputs border-danger" 
//                         maxLength="12" 
//                         placeholder="Máx. 12 letras" 
//                     />

//                     <button 
//                         onClick={handleRegister} 
//                         className={`Btn__Adsl--Red PluckingRooster__Instructions__Form__Button mt-5 ${isLoading ? 'opacity-50' : ''}`}
//                         disabled={isLoading || connectionStatus !== 'connected'}
//                     >
//                         {isLoading ? 'Conectando...' : 'Jugar ahora'}
//                     </button>

//                     <footer className="text-center mt-5 mb-4 FooterCredits">
//                         Hecho con ♥ por Interaktia | 2026
//                     </footer>
//                 </div>
//             )}

//             {/* PANTALLA 2: GAMEPAD */}
//             {step === 'playing' && (
//                 <div style={{ display: 'flex', width: '100%', height: '100%', userSelect: 'none' }}>
//                     {/* BOTÓN IZQUIERDA */}
//                     <div 
//                         style={{ flex: 1, backgroundColor: '#2980b9', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRight: '2px solid rgba(0,0,0,0.3)', cursor: 'pointer' }}
//                         onTouchStart={() => handleMove('left')}
//                         onTouchEnd={handleStop}
//                         onMouseDown={() => handleMove('left')}
//                         onMouseUp={handleStop}
//                         onMouseLeave={handleStop}
//                     >
//                         <i className="bi bi-caret-left-fill" style={{ fontSize: '10rem', color: 'rgba(255,255,255,0.5)' }}></i>
//                     </div>

//                     {/* BOTÓN DERECHA */}
//                     <div 
//                         style={{ flex: 1, backgroundColor: '#c0392b', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}
//                         onTouchStart={() => handleMove('right')}
//                         onTouchEnd={handleStop}
//                         onMouseDown={() => handleMove('right')}
//                         onMouseUp={handleStop}
//                         onMouseLeave={handleStop}
//                     >
//                         <i className="bi bi-caret-right-fill" style={{ fontSize: '10rem', color: 'rgba(255,255,255,0.5)' }}></i>
//                     </div>

//                     <div style={{ position: 'absolute', top: '10%', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
//                         <p style={{ background: 'rgba(0,0,0,0.5)', color: 'white', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold' }}>
//                             ¡MIRA LA PANTALLA GIGANTE!
//                         </p>
//                     </div>
//                 </div>
//             )}

//             {/* PANTALLA 3: RESULTADO FINAL */}
//             {/* {step === 'done' && (
//                 <div className="SelfieFan__Sent container h-100 d-flex flex-column justify-content-center">
//                     <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
//                     <p className="fs-1 mb-0 text-success text-center"><i className="bi bi-check-circle-fill"></i></p>
//                     <p className="SelfieFan__Sent__Title mt-3 mb-0 text-center">¡Tiempo Agotado!</p>
//                     <p className='Messages__Fan__Text--Center mb-0 mt-3'>Tu puntaje final es:</p>
//                     <p className='text-center fw-black' style={{ fontSize: '4rem', color: '#e74c3c', lineHeight: 1 }}>{finalScore}</p>
//                     <p className='Messages__Fan__Text--Center mb-0 mt-3 text-muted'>Preparando para el siguiente jugador...</p>
//                 </div>
//             )} */}

//             {/* PANTALLA 3: RESULTADO FINAL Y DECISIÓN */}
//             {step === 'done' && (
//                 <div className="SelfieFan__Sent container h-100 d-flex flex-column justify-content-center">
//                     <img src="/img/page/adsl-five-bicolor.png" className="LogoADSLxZtudia mt-3" alt="Logo" />
//                     <p className="fs-1 mb-0 text-success text-center"><i className="bi bi-check-circle-fill"></i></p>
//                     <p className="SelfieFan__Sent__Title mt-3 mb-0 text-center">¡Partida Terminada!</p>
//                     <p className='Messages__Fan__Text--Center mb-0 mt-3'>Puntaje obtenido:</p>
//                     <p className='text-center fw-black' style={{ fontSize: '4rem', color: '#e74c3c', lineHeight: 1 }}>{finalScore}</p>
                    
//                     <div className="d-flex flex-column gap-3 mt-5">
//                         <button onClick={handlePlayAgain} className="Btn__Adsl--RedOutline" disabled={isLoading}>
//                             <i className="bi bi-arrow-clockwise"></i> Dar segunda oportunidad
//                         </button>
//                         <button 
//                             onClick={() => {
//                                 setUserData({ fullName: '', nickname: '', email: '', phoneNumber: '', companionType: '' });
//                                 setStep('register');
//                             }} 
//                             className="Btn__Adsl--Red"
//                         >
//                             Siguiente Jugador <i className="bi bi-arrow-right"></i>
//                         </button>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export { CatchTheBall };